import React, { useMemo, useState, useEffect } from "react";
import { TrendingUp, TrendingDown } from "lucide-react";
import { PriceAlertEventData } from "@/api/price-alerts";
import {
  useTokenLivePrice,
  type LivePriceExchangeCode,
} from "@/hooks/useTokenLivePrice";
import { useInView } from "@/hooks/useInView";
import { ensureUsdtSymbol } from "@/utils/symbols";

const mapAlertExchangeToLiveCode = (
  exchange: string | undefined
): LivePriceExchangeCode => {
  const code = (exchange ?? "").toLowerCase();
  if (code.includes("binance")) return "binance";
  if (code.includes("bybit")) return "bybit";
  if (code.includes("gate")) return "gate";
  if (code.includes("kucoin")) return "kucoin";
  if (code.includes("aster")) return "asterdex";
  if (code.includes("hyperliquid")) return "hyperliquid";
  return "dynamic";
};

interface PriceAlertEventTileProps {
  event: PriceAlertEventData;
  onAnalyze?: (event: PriceAlertEventData) => void;
  onTrade?: (event: PriceAlertEventData) => void;
}

const formatPrice = (price: number) =>
  `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })}`;

const formatTimeAgo = (dateStr: string) => {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diffMs / 60_000);
  const hours = Math.floor(diffMs / 3_600_000);
  const days = Math.floor(diffMs / 86_400_000);

  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
};

const formatDateTime = (dateStr: string) => {
  const d = new Date(dateStr);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const PriceAlertEventTile: React.FC<PriceAlertEventTileProps> = ({
  event,
  onAnalyze,
  onTrade,
}) => {
  const isSpike = event.alertType.toLowerCase() === "spike";
  const sign = event.changePercent >= 0 ? "+" : "";

  const [tileRef, isInView] = useInView<HTMLDivElement>({
    rootMargin: "50px",
    threshold: 0.1,
  });

  const normalizedSymbol = useMemo(
    () => ensureUsdtSymbol(event.symbol),
    [event.symbol]
  );

  const liveExchangeCode = useMemo(
    () => mapAlertExchangeToLiveCode(event.exchange),
    [event.exchange]
  );

  const { price: livePrice, isSubscribed } = useTokenLivePrice(
    normalizedSymbol,
    liveExchangeCode,
    isInView
  );

  const [priceLoadTimeout, setPriceLoadTimeout] = useState(false);

  useEffect(() => {
    if (isInView && livePrice == null) {
      setPriceLoadTimeout(false);
      const timer = setTimeout(() => {
        setPriceLoadTimeout(true);
      }, 8000);

      return () => clearTimeout(timer);
    }
  }, [isInView, livePrice]);

  const isPriceLoading = isInView && livePrice == null && !priceLoadTimeout;

  const liveChangePercent = useMemo(() => {
    if (livePrice == null || event.startPrice === 0) return null;
    return ((livePrice - event.startPrice) / event.startPrice) * 100;
  }, [livePrice, event.startPrice]);

  const liveChangeSign =
    liveChangePercent != null && liveChangePercent >= 0 ? "+" : "";

  const liveChangeColorClass = useMemo(() => {
    if (liveChangePercent == null) return "text-text-secondary";
    return liveChangePercent >= 0 ? "text-green-400" : "text-red-400";
  }, [liveChangePercent]);

  const livePulse = isSubscribed ? (
    <span className="relative inline-flex h-2 w-2 shrink-0" aria-hidden>
      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400/70" />
      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
    </span>
  ) : null;

  const liveChangeValues =
    liveChangePercent == null || livePrice == null ? (
      <span className="text-text-secondary">—</span>
    ) : (
      <span className="inline-flex items-center gap-2 tabular-nums">
        <span className="text-xs font-medium text-text-secondary">
          {formatPrice(livePrice)}
        </span>
        <span
          className={`font-semibold text-sm lg:text-xs ${liveChangeColorClass} ${
            isSubscribed ? "transition-colors duration-150" : ""
          }`}
        >
          {liveChangeSign}
          {liveChangePercent.toFixed(2)}%
        </span>
      </span>
    );

  const liveStripValues = isPriceLoading ? (
    <div className="inline-flex items-center gap-2 tabular-nums">
      <div className="h-4 bg-gray-600/30 rounded animate-pulse w-[4.5rem]" />
      <div className="h-4 bg-gray-600/30 rounded animate-pulse w-12" />
    </div>
  ) : (
    liveChangeValues
  );

  return (
    <div
      ref={tileRef}
      className="bg-secondary rounded-lg p-3 border border-gray-500/30"
    >
      {/* Header */}
      <div className="flex items-start justify-between border-b border-gray-500/30 pb-2">
        <div className="flex items-center gap-2">
          {isSpike ? (
            <TrendingUp className="w-5 h-5 text-green-400" />
          ) : (
            <TrendingDown className="w-5 h-5 text-red-400" />
          )}
          <h3 className="text-lg font-bold text-text-primary">
            {event.symbol}
          </h3>
          <span
            className={`px-2 py-0.5 rounded-full text-xs ${
              isSpike
                ? "bg-green-500/20 text-green-400"
                : "bg-red-500/20 text-red-400"
            }`}
          >
            {isSpike ? "Spike" : "Drop"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-accent rounded-full px-2 py-0.5 bg-accent/10 capitalize">
            {event.exchange?.replace("_", " ")}
          </span>
          <span className="text-xs text-text-secondary">
            {formatTimeAgo(event.timestamp)}
          </span>
        </div>
      </div>

      {/* Mobile / tablet: equal-height columns + full-width live strip */}
      <div className="pt-2 space-y-3 lg:hidden">
        <div className="flex items-stretch">
          <div className="flex-1 min-w-0 flex flex-col border-r border-gray-500/30 pr-3">
            <p className="text-xs text-text-secondary mb-1">Change</p>
            <p
              className={`text-base font-semibold tabular-nums leading-tight ${
                event.changePercent >= 0 ? "text-green-400" : "text-red-400"
              }`}
            >
              {sign}
              {event.changePercent.toFixed(2)}%
            </p>
          </div>

          <div className="flex-1 min-w-0 flex flex-col border-r border-gray-500/30 px-3">
            <p className="text-xs text-text-secondary mb-1">Price</p>
            <p className="text-sm font-semibold text-text-primary tabular-nums leading-tight">
              {formatPrice(event.currentPrice)}
            </p>
            <p className="text-[11px] text-text-secondary mt-1 leading-snug">
              From {formatPrice(event.startPrice)}
            </p>
          </div>

          <div className="flex-1 min-w-0 flex flex-col pl-3">
            <p className="text-xs text-text-secondary mb-1">Timeframe</p>
            <p className="text-sm font-semibold text-text-primary leading-tight">
              {event.interval}
            </p>
            <p className="text-[11px] text-text-secondary mt-1 leading-snug">
              {formatDateTime(event.timestamp)}
            </p>
          </div>
        </div>

        <div className="rounded-lg bg-black/35 border border-gray-500/25 px-3 py-2.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[11px] font-medium uppercase tracking-wide text-text-secondary">
              Live price
            </span>
            {livePulse}
          </div>
          <p className="text-sm font-semibold tabular-nums shrink-0 text-right">
            {liveStripValues}
          </p>
        </div>
      </div>

      {/* Desktop (lg+): single metrics row + full-width live strip (matches app desktop breakpoint 1024px) */}
      <div className="hidden lg:block pt-2 space-y-3">
        <div className="flex items-stretch justify-start gap-3 sm:gap-4">
          <div className="flex flex-col items-start flex-1 min-w-0">
            <p className="text-xs text-text-secondary mb-1">Change</p>
            <p
              className={`text-sm font-semibold tabular-nums ${
                event.changePercent >= 0 ? "text-green-400" : "text-red-400"
              }`}
            >
              {sign}
              {event.changePercent.toFixed(2)}%
            </p>
          </div>

          <div className="w-px bg-gray-500/30 flex-shrink-0 self-stretch" />

          <div className="flex flex-col items-start flex-1 min-w-0">
            <p className="text-xs text-text-secondary mb-1">Start Price</p>
            <p className="text-sm text-text-primary font-medium tabular-nums truncate w-full">
              {formatPrice(event.startPrice)}
            </p>
          </div>

          <div className="w-px bg-gray-500/30 flex-shrink-0 self-stretch" />

          <div className="flex flex-col items-start flex-1 min-w-0">
            <p className="text-xs text-text-secondary mb-1">Current Price</p>
            <p className="text-sm text-text-primary font-medium tabular-nums truncate w-full">
              {formatPrice(event.currentPrice)}
            </p>
          </div>

          <div className="w-px bg-gray-500/30 flex-shrink-0 self-stretch" />

          <div className="flex flex-col items-start flex-1 min-w-0">
            <p className="text-xs text-text-secondary mb-1">Timeframe</p>
            <p className="text-sm text-text-primary font-medium">
              {event.interval}
            </p>
          </div>

          <div className="w-px bg-gray-500/30 flex-shrink-0 self-stretch" />

          <div className="flex flex-col items-start flex-[1.25] min-w-0">
            <p className="text-xs text-text-secondary mb-1">Date & Time</p>
            <p className="text-sm text-text-primary font-medium truncate w-full">
              {formatDateTime(event.timestamp)}
            </p>
          </div>
        </div>

        <div className="rounded-lg bg-black/35 border border-gray-500/25 px-3 py-2.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[11px] font-medium uppercase tracking-wide text-text-secondary">
              Live price
            </span>
            {livePulse}
          </div>
          <p className="text-sm font-semibold tabular-nums shrink-0 text-right">
            {liveStripValues}
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 mt-3 pt-3 border-t border-gray-500/30">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onAnalyze?.(event);
          }}
          className="flex-1 px-4 py-2 rounded-lg bg-gray-600/20 text-text-primary hover:bg-gray-600/30 transition-colors text-sm font-medium"
        >
          Analyze
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onTrade?.(event);
          }}
          className="flex-1 px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors text-sm font-medium"
        >
          Trade
        </button>
      </div>
    </div>
  );
};
